import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Eye, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';

import { JobPostingProgress } from './JobPostingProgress';
import { JobPostingStatusBadge } from './JobPostingStatusBadge';

import type { JobPosting } from '@/types/jobPosting.types';
import type { PaginationMeta } from '@/types/submission.types';

interface JobPostingsTableProps {
  postings: JobPosting[];
  isLoading: boolean;
  onRowClick: (posting: JobPosting) => void;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (field: string) => void;
  meta: PaginationMeta | null;
  onPageChange: (page: number) => void;
}

const SORTABLE_COLUMNS = ['postingNumber', 'title', 'createdAt'] as const;

function SortIcon({ field, sortBy, sortOrder }: { field: string; sortBy: string; sortOrder: 'asc' | 'desc' }) {
  if (field !== sortBy) {
    return <ChevronUp className='h-4 w-4 text-gray-300' />;
  }
  return sortOrder === 'asc' ? (
    <ChevronUp className='h-4 w-4 text-gray-700' />
  ) : (
    <ChevronDown className='h-4 w-4 text-gray-700' />
  );
}

function SkeletonRow() {
  return (
    <tr className='animate-pulse'>
      {Array.from({ length: 9 }).map((_, i) => (
        <td key={i} className='px-4 py-3'>
          <div className='h-4 rounded bg-gray-200' />
        </td>
      ))}
    </tr>
  );
}

export function JobPostingsTable({
  postings,
  isLoading,
  onRowClick,
  sortBy,
  sortOrder,
  onSort,
  meta,
  onPageChange,
}: JobPostingsTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isSortable = (field: string): boolean => {
    return (SORTABLE_COLUMNS as readonly string[]).includes(field);
  };

  const handleHeaderClick = (field: string) => {
    if (isSortable(field)) {
      onSort(field);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns = [
    { key: 'postingNumber', label: t('jobPostings.columns.postingNumber') },
    { key: 'title', label: t('jobPostings.columns.title') },
    { key: 'role', label: t('jobPostings.columns.role') },
    { key: 'location', label: t('jobPostings.columns.location') },
    { key: 'status', label: t('jobPostings.columns.status') },
    { key: 'capacity', label: t('jobPostings.columns.capacity') },
    { key: 'applications', label: t('jobPostings.columns.applications') },
    { key: 'createdAt', label: t('jobPostings.columns.createdAt') },
    { key: 'actions', label: t('jobPostings.columns.actions') },
  ];

  const startItem = meta ? (meta.page - 1) * meta.limit + 1 : 0;
  const endItem = meta ? Math.min(meta.page * meta.limit, meta.total) : 0;

  return (
    <div className='overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm'>
      <div className='overflow-x-auto'>
        <table className='min-w-full divide-y divide-gray-200'>
          <thead className='bg-gray-50'>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500',
                    isSortable(col.key) && 'cursor-pointer select-none hover:text-gray-700',
                  )}
                  onClick={() => handleHeaderClick(col.key)}>
                  <div className='flex items-center gap-1'>
                    {col.label}
                    {isSortable(col.key) && <SortIcon field={col.key} sortBy={sortBy} sortOrder={sortOrder} />}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : postings.length === 0 ? (
              <tr>
                <td colSpan={9} className='px-4 py-12 text-center text-sm text-gray-500'>
                  {t('jobPostings.noResults')}
                </td>
              </tr>
            ) : (
              postings.map((posting) => (
                <tr
                  key={posting.jobPostingId}
                  className='cursor-pointer hover:bg-gray-50'
                  onClick={() => onRowClick(posting)}>
                  <td className='whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900'>
                    {posting.postingNumber}
                  </td>
                  <td className='px-4 py-3 text-sm text-gray-900'>{posting.title}</td>
                  <td className='whitespace-nowrap px-4 py-3 text-sm text-gray-500'>
                    {t(`jobPostings.roles.${posting.role}`)}
                  </td>
                  <td className='whitespace-nowrap px-4 py-3 text-sm text-gray-500'>
                    {posting.siteCity}, {posting.siteState}
                  </td>
                  <td className='whitespace-nowrap px-4 py-3'>
                    <JobPostingStatusBadge status={posting.status} />
                  </td>
                  <td className='px-4 py-3'>
                    <JobPostingProgress filledCount={posting.filledCount} capacity={posting.capacity} />
                  </td>
                  <td className='whitespace-nowrap px-4 py-3 text-sm text-gray-500'>
                    {posting.applicationCounts?.total ?? 0}
                  </td>
                  <td className='whitespace-nowrap px-4 py-3 text-sm text-gray-500'>{formatDate(posting.createdAt)}</td>
                  <td className='whitespace-nowrap px-4 py-3'>
                    <div className='flex items-center gap-1'>
                      <button
                        title={t('jobPostings.actions.edit')}
                        className='rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                        onClick={(e) => {
                          e.stopPropagation();
                          onRowClick(posting);
                        }}>
                        <Pencil className='h-4 w-4' />
                      </button>
                      <button
                        title={t('jobPostings.actions.viewSubmissions')}
                        className='rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                        onClick={(e) => {
                          e.stopPropagation();
                          void navigate(`/dashboard?jobPostingId=${posting.jobPostingId}`);
                        }}>
                        <Eye className='h-4 w-4' />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {meta && meta.totalPages > 1 && (
        <div className='flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3'>
          <p className='text-sm text-gray-500'>
            {t('jobPostings.pagination.showing', { from: startItem, to: endItem, total: meta.total })}
          </p>
          <div className='flex items-center gap-2'>
            <button
              type='button'
              className={cn(
                'rounded-md border border-gray-300 p-1.5',
                meta.page <= 1 ? 'cursor-not-allowed text-gray-300' : 'text-gray-600 hover:bg-gray-50',
              )}
              disabled={meta.page <= 1}
              onClick={() => onPageChange(meta.page - 1)}>
              <ChevronLeft className='h-4 w-4' />
            </button>
            <span className='text-sm text-gray-700'>
              {t('jobPostings.pagination.page', { current: meta.page, total: meta.totalPages })}
            </span>
            <button
              type='button'
              className={cn(
                'rounded-md border border-gray-300 p-1.5',
                meta.page >= meta.totalPages ? 'cursor-not-allowed text-gray-300' : 'text-gray-600 hover:bg-gray-50',
              )}
              disabled={meta.page >= meta.totalPages}
              onClick={() => onPageChange(meta.page + 1)}>
              <ChevronRight className='h-4 w-4' />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
